/**
 * Vendor Bot Shop - Customer Profile Page
 * User profile, account info, settings
 */

import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { BottomNavigation } from '../../components/customer';
import { useTelegram } from '../../hooks/useTelegram';
import { useTranslation } from '../../hooks/useTranslation';

interface CustomerProfile {
  id: number;
  telegram_id: number;
  telegram_username: string | null;
  first_name: string;
  last_name: string | null;
  wallet_balance: number;
  points_balance: number;
  total_orders: number;
  total_spent: number;
  is_verified: boolean;
  referral_code: string;
  language: string;
  created_at: string;
  last_seen_at: string;
}

interface SavedAddress {
  id: number;
  label: string;
  full_name: string;
  address_line1: string;
  address_line2?: string;
  city: string;
  state?: string;
  postal_code: string;
  country: string;
  phone: string;
  is_default: boolean;
}

interface StoreInfo {
  currency_symbol: string;
  currency_code: string;
}

const PageContainer = styled.div`
  min-height: 100vh;
  background: var(--tg-theme-bg-color, #1C1C1E);
  padding-bottom: calc(70px + env(safe-area-inset-bottom, 0px));
  max-width: 768px;
  margin: 0 auto;

  @media (min-width: 375px) {
    padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px));
  }

  @media (min-width: 768px) {
    max-width: 900px;
  }
`;

const Header = styled.div`
  padding: 12px;
  text-align: center;
  border-bottom: 1px solid var(--tg-theme-hint-color, #3a3a3c);

  @media (min-width: 375px) {
    padding: 16px;
  }
`;

const HeaderTitle = styled.h1`
  font-size: 18px;
  font-weight: 600;
  color: var(--tg-theme-text-color, #fff);
  margin: 0;

  @media (min-width: 375px) {
    font-size: 20px;
  }
`;

const ProfileCard = styled.div`
  margin: 12px;
  padding: 16px;
  background: var(--tg-theme-secondary-bg-color, #2C2C2E);
  border-radius: 12px;
  text-align: center;

  @media (min-width: 375px) {
    margin: 16px;
    padding: 24px;
    border-radius: 16px;
  }
`;

const Avatar = styled.div`
  width: 64px;
  height: 64px;
  margin: 0 auto 12px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--tg-theme-button-color, #4CAF50) 0%, #45a049 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  color: #fff;
  font-weight: 700;

  @media (min-width: 375px) {
    width: 80px;
    height: 80px;
    margin: 0 auto 16px;
    font-size: 32px;
  }
`;

const UserName = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: var(--tg-theme-text-color, #fff);
  margin: 0 0 4px;

  @media (min-width: 375px) {
    font-size: 22px;
  }
`;

const UserUsername = styled.div`
  font-size: 15px;
  color: var(--tg-theme-hint-color, #8e8e93);
  margin-bottom: 16px;
`;

const VerifiedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: rgba(76, 175, 80, 0.2);
  color: #4CAF50;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--tg-theme-hint-color, #3a3a3c);

  @media (min-width: 375px) {
    gap: 12px;
    margin-top: 20px;
    padding-top: 20px;
  }
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: var(--tg-theme-text-color, #fff);

  @media (min-width: 375px) {
    font-size: 20px;
  }
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: var(--tg-theme-hint-color, #8e8e93);
  margin-top: 4px;
`;

const SectionHeader = styled.div`
  padding: 16px;
  padding-bottom: 8px;
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: var(--tg-theme-hint-color, #8e8e93);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0;
`;

const InfoCard = styled.div`
  margin: 0 12px 12px;
  background: var(--tg-theme-secondary-bg-color, #2C2C2E);
  border-radius: 10px;
  overflow: hidden;

  @media (min-width: 375px) {
    margin: 0 16px 16px;
    border-radius: 12px;
  }
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid var(--tg-theme-hint-color, #3a3a3c);

  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.div`
  font-size: 15px;
  color: var(--tg-theme-text-color, #fff);
  display: flex;
  align-items: center;
  gap: 10px;
`;

const InfoValue = styled.div`
  font-size: 15px;
  color: var(--tg-theme-hint-color, #8e8e93);
`;

const MenuList = styled.div`
  margin: 0 12px;

  @media (min-width: 375px) {
    margin: 0 16px;
  }
`;

const MenuItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 14px;
  background: var(--tg-theme-secondary-bg-color, #2C2C2E);
  border-radius: 10px;
  margin-bottom: 6px;
  cursor: pointer;

  &:active {
    opacity: 0.7;
  }

  @media (min-width: 375px) {
    padding: 14px 16px;
    border-radius: 12px;
    margin-bottom: 8px;
  }
`;

const MenuInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const MenuIcon = styled.span`
  font-size: 20px;
`;

const MenuLabel = styled.span`
  font-size: 15px;
  color: var(--tg-theme-text-color, #fff);
`;

const MenuArrow = styled.span`
  font-size: 18px;
  color: var(--tg-theme-hint-color, #8e8e93);
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: var(--tg-theme-hint-color, #8e8e93);
`;

const ReferralRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  cursor: pointer;

  &:active {
    opacity: 0.7;
  }
`;

const ReferralInfo = styled.div`
  flex: 1;
`;

const ReferralLink = styled.div`
  font-size: 13px;
  color: var(--tg-theme-link-color, #00ff88);
  margin-top: 4px;
  word-break: break-all;
`;

const CopyButton = styled.button<{ $copied: boolean }>`
  padding: 8px 16px;
  border-radius: 8px;
  border: none;
  background: ${props => props.$copied
    ? 'rgba(76, 175, 80, 0.2)'
    : 'var(--tg-theme-button-color, #4CAF50)'};
  color: ${props => props.$copied
    ? '#4CAF50'
    : 'var(--tg-theme-button-text-color, #fff)'};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
`;

const EmptyAddresses = styled.div`
  text-align: center;
  padding: 24px 16px;
  color: var(--tg-theme-hint-color, #8e8e93);

  span {
    font-size: 32px;
    display: block;
    margin-bottom: 8px;
  }

  p {
    margin: 0;
    font-size: 14px;
  }
`;

const AddressRow = styled.div`
  padding: 14px 16px;
  border-bottom: 1px solid var(--tg-theme-hint-color, #3a3a3c);

  &:last-child {
    border-bottom: none;
  }
`;

const AddressInfo = styled.div`
  flex: 1;
`;

const AddressLabel = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: var(--tg-theme-text-color, #fff);
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
`;

const DefaultBadge = styled.span`
  font-size: 10px;
  font-weight: 500;
  padding: 2px 6px;
  background: rgba(76, 175, 80, 0.2);
  color: #4CAF50;
  border-radius: 4px;
`;

const AddressDetails = styled.div`
  font-size: 13px;
  color: var(--tg-theme-hint-color, #8e8e93);
  line-height: 1.4;
`;

const ShopProfile: React.FC = () => {
  const { botUsername } = useParams<{ botUsername: string }>();
  const navigate = useNavigate();
  const { user } = useTelegram();
  const { t } = useTranslation();
  const [profile, setProfile] = useState<CustomerProfile | null>(null);
  const [addresses, setAddresses] = useState<SavedAddress[]>([]);
  const [storeInfo, setStoreInfo] = useState<StoreInfo>({ currency_symbol: '$', currency_code: 'USD' });
  const [isLoading, setIsLoading] = useState(true);
  const [copiedReferral, setCopiedReferral] = useState(false);

  const baseUrl = process.env.REACT_APP_API_URL || '';

  const loadProfile = useCallback(async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('auth_token');
      const headers: HeadersInit = token ? { 'Authorization': `Bearer ${token}` } : {};

      // Fetch customer profile
      const profileRes = await fetch(`${baseUrl}/api/shop/${botUsername}/customer/profile`, { headers });
      const profileJson = await profileRes.json();

      if (profileJson.success) {
        setProfile(profileJson.data);
      }

      // Fetch saved addresses
      try {
        const addressRes = await fetch(`${baseUrl}/api/shop/${botUsername}/customer/addresses`, { headers });
        const addressJson = await addressRes.json();
        if (addressJson.success && addressJson.data) {
          setAddresses(addressJson.data);
        }
      } catch (e) {
        // Addresses endpoint may not exist yet
        console.log('Addresses not available');
      }

      // Fetch store info for currency
      const storeRes = await fetch(`${baseUrl}/api/shop/${botUsername}/store-info`);
      const storeJson = await storeRes.json();

      if (storeJson.success) {
        setStoreInfo({
          currency_symbol: storeJson.data.currency_symbol || '$',
          currency_code: storeJson.data.currency || 'USD'
        });
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setIsLoading(false);
    }
  }, [botUsername, baseUrl]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleMenuClick = (path: string) => {
    window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('light');
    navigate(path);
  };

  const copyReferralLink = () => {
    if (profile?.referral_code) {
      const referralLink = `https://t.me/${botUsername}?start=${profile.referral_code}`;
      navigator.clipboard.writeText(referralLink).then(() => {
        setCopiedReferral(true);
        window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
        setTimeout(() => setCopiedReferral(false), 2000);
      }).catch(() => {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = referralLink;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopiedReferral(true);
        setTimeout(() => setCopiedReferral(false), 2000);
      });
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getInitials = (): string => {
    if (profile?.first_name) {
      return profile.first_name.charAt(0).toUpperCase();
    }
    if (user?.first_name) {
      return user.first_name.charAt(0).toUpperCase();
    }
    return 'U';
  };

  const getFullName = (): string => {
    if (profile) {
      return [profile.first_name, profile.last_name].filter(Boolean).join(' ') || 'User';
    }
    if (user) {
      return [user.first_name, user.last_name].filter(Boolean).join(' ') || 'User';
    }
    return 'User';
  };

  const menuItems = [
    { icon: '📦', label: t('profile.myOrders'), path: `/shop/${botUsername}/dashboard?tab=orders` },
    { icon: '📥', label: t('dashboard.downloads'), path: `/shop/${botUsername}/dashboard?tab=downloads` },
    { icon: '🔑', label: t('profile.licenseKeys'), path: `/shop/${botUsername}/dashboard?tab=keys` },
    { icon: '⭐', label: t('dashboard.subs'), path: `/shop/${botUsername}/dashboard?tab=subscriptions` },
    { icon: '💰', label: t('wallet.wallet'), path: `/shop/${botUsername}/wallet` },
  ];

  if (isLoading) {
    return (
      <PageContainer>
        <Header>
          <HeaderTitle>{t('profile.profile')}</HeaderTitle>
        </Header>
        <LoadingState>{t('profile.loadingProfile')}</LoadingState>
        <BottomNavigation />
      </PageContainer>
    );
  }

  const currencySymbol = storeInfo.currency_symbol;

  return (
    <PageContainer>
      <Header>
        <HeaderTitle>{t('profile.profile')}</HeaderTitle>
      </Header>

      <ProfileCard>
        <Avatar>{getInitials()}</Avatar>
        <UserName>{getFullName()}</UserName>
        <UserUsername>
          @{profile?.telegram_username || user?.username || 'username'}
        </UserUsername>
        {profile?.is_verified && (
          <VerifiedBadge>✓ {t('profile.verified')}</VerifiedBadge>
        )}
        <StatsRow>
          <StatItem>
            <StatValue>{profile?.total_orders || 0}</StatValue>
            <StatLabel>{t('profile.orders')}</StatLabel>
          </StatItem>
          <StatItem>
            <StatValue>{currencySymbol}{parseFloat(String(profile?.wallet_balance || 0)).toFixed(2)}</StatValue>
            <StatLabel>{t('profile.balance')}</StatLabel>
          </StatItem>
          <StatItem>
            <StatValue>{(profile?.points_balance || 0).toLocaleString()}</StatValue>
            <StatLabel>{t('profile.points')}</StatLabel>
          </StatItem>
        </StatsRow>
      </ProfileCard>

      <SectionHeader>
        <SectionTitle>{t('profile.referralLink')}</SectionTitle>
      </SectionHeader>

      <InfoCard>
        <ReferralRow onClick={copyReferralLink}>
          <ReferralInfo>
            <InfoLabel>🔗 {t('profile.yourReferralLink')}</InfoLabel>
            <ReferralLink>
              {profile?.referral_code
                ? `t.me/${botUsername}?start=${profile.referral_code}`
                : t('profile.notAvailable')}
            </ReferralLink>
          </ReferralInfo>
          <CopyButton $copied={copiedReferral}>
            {copiedReferral ? `✓ ${t('common.copied')}` : t('common.copy')}
          </CopyButton>
        </ReferralRow>
      </InfoCard>

      <SectionHeader>
        <SectionTitle>{t('profile.savedAddresses')}</SectionTitle>
      </SectionHeader>

      <InfoCard>
        {addresses.length === 0 ? (
          <EmptyAddresses>
            <span>📍</span>
            <p>{t('profile.noSavedAddresses')}</p>
          </EmptyAddresses>
        ) : (
          addresses.map((address) => (
            <AddressRow key={address.id}>
              <AddressInfo>
                <AddressLabel>
                  {address.label || t('checkout.address')}
                  {address.is_default && <DefaultBadge>{t('profile.default')}</DefaultBadge>}
                </AddressLabel>
                <AddressDetails>
                  {address.full_name}, {address.address_line1}
                  {address.address_line2 && `, ${address.address_line2}`}
                  , {address.city}
                  {address.state && `, ${address.state}`}
                  , {address.postal_code}, {address.country}
                </AddressDetails>
              </AddressInfo>
            </AddressRow>
          ))
        )}
      </InfoCard>

      <SectionHeader>
        <SectionTitle>{t('profile.accountInfo')}</SectionTitle>
      </SectionHeader>

      <InfoCard>
        <InfoRow>
          <InfoLabel>🌐 {t('profile.language')}</InfoLabel>
          <InfoValue>{profile?.language?.toUpperCase() || 'EN'}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>📅 {t('profile.memberSince')}</InfoLabel>
          <InfoValue>{profile?.created_at ? formatDate(profile.created_at) : '-'}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>💵 {t('profile.totalSpent')}</InfoLabel>
          <InfoValue>{currencySymbol}{parseFloat(String(profile?.total_spent || 0)).toFixed(2)}</InfoValue>
        </InfoRow>
      </InfoCard>

      <SectionHeader>
        <SectionTitle>{t('profile.quickAccess')}</SectionTitle>
      </SectionHeader>

      <MenuList>
        {menuItems.map((item, index) => (
          <MenuItem key={index} onClick={() => handleMenuClick(item.path)}>
            <MenuInfo>
              <MenuIcon>{item.icon}</MenuIcon>
              <MenuLabel>{item.label}</MenuLabel>
            </MenuInfo>
            <MenuArrow>›</MenuArrow>
          </MenuItem>
        ))}
      </MenuList>

      <BottomNavigation />
    </PageContainer>
  );
};

export default ShopProfile;
